import React, { useState } from 'react';
import styled from 'styled-components';

import CheckIconAlt from '../../svgs/CheckIconAlt';
import DeleteIcon from '../../svgs/DeleteIcon';
import { extractErrorInfo } from '../../utility/error';
import { LogManager } from '../../utility/Logger';
import {
  DestructiveButton,
  DialogCard,
  DialogFooter,
  DialogHeader,
  DialogText,
  DialogTitle,
  SecondaryButton,
} from '../common/dialogStyles';
import Modal from '../common/Modal';

import type { DropdownOption } from './hooks/useVideoSelector';

interface DeleteVideosModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedVideoIds: string[]) => Promise<void>;
  mainVideo: DropdownOption | null;
}

const logger = LogManager.getLogger('DeleteVideosModal');

const collectAllVideos = (
  mainVideo: DropdownOption,
): Array<{ id: string; label: string; isMain: boolean }> => {
  const allVideos: Array<{ id: string; label: string; isMain: boolean }> = [];

  // Add main video
  if (mainVideo.id && mainVideo.id !== 'original_story') {
    allVideos.push({
      id: mainVideo.id,
      label: `${mainVideo.aspectRatio || 'Unknown'} version`,
      isMain: true,
    });
  }

  // Add associated videos
  if (mainVideo.associatedVideos?.length) {
    mainVideo.associatedVideos.forEach(assocVideo => {
      if (assocVideo.id) {
        allVideos.push({
          id: assocVideo.id,
          label: `${assocVideo.aspectRatio || 'Unknown'} version`,
          isMain: false,
        });
      }
    });
  }

  return allVideos;
};

export const DeleteVideosModal: React.FC<DeleteVideosModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  mainVideo,
}) => {
  const [selectedVideoIds, setSelectedVideoIds] = useState<Set<string>>(
    new Set(),
  );
  const [isDeleting, setIsDeleting] = useState(false);

  if (!mainVideo) return null;

  const allVideos = collectAllVideos(mainVideo);

  const toggleVideoSelection = (videoId: string) => {
    setSelectedVideoIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  const handleConfirm = async () => {
    if (selectedVideoIds.size === 0) {
      onClose();
      return;
    }

    setIsDeleting(true);
    try {
      await onConfirm(Array.from(selectedVideoIds));
      setSelectedVideoIds(new Set());
      onClose();
    } catch (error) {
      logger.error('Error deleting videos', extractErrorInfo(error));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setSelectedVideoIds(new Set());
    onClose();
  };

  return (
    <Modal isOpen={isOpen} closeModal={handleClose}>
      <DialogCard>
        <DialogHeader>
          <DialogTitle>Delete Clip</DialogTitle>
          <DialogText>
            Choose one or more versions of this clip to delete.
          </DialogText>
        </DialogHeader>

        <VersionList>
          {allVideos.map(video => (
            <VersionRow
              key={video.id}
              onClick={() => !isDeleting && toggleVideoSelection(video.id)}
              $disabled={isDeleting}
            >
              <CheckBox $checked={selectedVideoIds.has(video.id)} role="button">
                {selectedVideoIds.has(video.id) && <CheckIconAlt />}
              </CheckBox>
              <VersionLabel>{video.label}</VersionLabel>
            </VersionRow>
          ))}
        </VersionList>

        <DialogFooter>
          <SecondaryButton
            type="button"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancel
          </SecondaryButton>
          <DestructiveButton
            type="button"
            onClick={handleConfirm}
            disabled={isDeleting || selectedVideoIds.size === 0}
          >
            <DeleteIcon width="16" height="16" strokeColor="currentColor" />
            {isDeleting ? 'Deleting...' : 'Delete Selected'}
          </DestructiveButton>
        </DialogFooter>
      </DialogCard>
    </Modal>
  );
};

const VersionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const VersionRow = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  user-select: none;
  cursor: pointer;

  ${props =>
    props.$disabled &&
    `
    cursor: not-allowed;
    opacity: 0.6;
  `}
`;

const CheckBox = styled.div<{ $checked: boolean }>`
  background-color: ${props =>
    props.$checked ? 'var(--accent)' : 'transparent'};
  width: 16px;
  height: 16px;
  border-radius: 2px;
  overflow: hidden;
  border: 1px solid
    ${props => (props.$checked ? 'var(--accent)' : 'var(--text-muted)')};
  box-sizing: border-box;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VersionLabel = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--foreground);
`;
